const ACCESS_TOKEN_KEY = 'jw.access_token';
const REFRESH_TOKEN_KEY = 'jw.refresh_token';
// Where an admin's own session waits while they are signed in as somebody else.
// The server never invalidates it, so stopping is putting this back.
const IMPERSONATION_BACKUP_KEY = 'jw.impersonation_backup';

const listeners = new Set();

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private browsing / storage disabled.
    return null;
  }
}

function safeSet(key, value) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export const tokenStorage = {
  getAccessToken() {
    return safeGet(ACCESS_TOKEN_KEY);
  },

  getRefreshToken() {
    return safeGet(REFRESH_TOKEN_KEY);
  },

  set(tokens) {
    safeSet(ACCESS_TOKEN_KEY, tokens.access_token);
    safeSet(REFRESH_TOKEN_KEY, tokens.refresh_token);
    listeners.forEach((listener) => listener(tokens));
  },

  setAccessToken(accessToken) {
    safeSet(ACCESS_TOKEN_KEY, accessToken);
  },

  clear() {
    safeSet(ACCESS_TOKEN_KEY, null);
    safeSet(REFRESH_TOKEN_KEY, null);
    safeSet(IMPERSONATION_BACKUP_KEY, null);
    listeners.forEach((listener) => listener(null));
  },

  hasSession() {
    return Boolean(safeGet(ACCESS_TOKEN_KEY));
  },

  /**
   * Step into somebody else's session, keeping the caller's own to come back to.
   *
   * The impersonation token has no refresh token by design, so the refresh
   * token is cleared rather than kept alongside: leaving the admin's own
   * refresh token in place would let the API client silently swap the session
   * back to the admin on the first 401, which looks like nothing at all
   * happened and is exactly the kind of confusion an impersonated session
   * must not create.
   */
  startImpersonation(accessToken) {
    const backup = JSON.stringify({
      access_token: safeGet(ACCESS_TOKEN_KEY),
      refresh_token: safeGet(REFRESH_TOKEN_KEY),
    });
    safeSet(IMPERSONATION_BACKUP_KEY, backup);
    safeSet(ACCESS_TOKEN_KEY, accessToken);
    safeSet(REFRESH_TOKEN_KEY, null);
    listeners.forEach((listener) => listener({ access_token: accessToken }));
  },

  /** Put the admin's own session back. Returns false if there was none. */
  stopImpersonation() {
    const raw = safeGet(IMPERSONATION_BACKUP_KEY);
    if (!raw) return false;
    safeSet(IMPERSONATION_BACKUP_KEY, null);
    try {
      const tokens = JSON.parse(raw);
      safeSet(ACCESS_TOKEN_KEY, tokens.access_token ?? null);
      safeSet(REFRESH_TOKEN_KEY, tokens.refresh_token ?? null);
      listeners.forEach((listener) => listener(tokens));
      return true;
    } catch {
      // A corrupt backup is worse than none: leave them signed out rather than
      // half-restored into a session nobody can account for.
      safeSet(ACCESS_TOKEN_KEY, null);
      safeSet(REFRESH_TOKEN_KEY, null);
      listeners.forEach((listener) => listener(null));
      return false;
    }
  },

  isImpersonating() {
    return Boolean(safeGet(IMPERSONATION_BACKUP_KEY));
  },

  /** Subscribe to token changes (used to sign the user out across tabs). */
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
